
import { useOrganizationCoreSettings } from "./useOrganizationCoreSettings";
import { useUpdateBillingSettings } from "./useUpdateBillingSettings";
import { useUpdateClassDefaults } from "./useUpdateClassDefaults";
import { useUpdateParentPortalSettings } from "./useUpdateParentPortalSettings";
import { useUpdateOrganizationSettings } from "./useUpdateOrganizationSettings";
import { Organization, BillingSettings, ClassDefaults } from "@/types/organizationTypes";

// Change from `export` to `export type`
export type ParentPortalSettings = {
  features?: {
    classEnrollments?: boolean;
    onlinePayments?: boolean;
    messaging?: boolean;
    documentAccess?: boolean;
  };
  appearance?: {
    theme?: string;
    logoEnabled?: boolean;
    customHeaderColor?: string;
    customAccentColor?: string;
  };
  permissions?: {
    canUpdateProfile?: boolean;
    canViewInvoices?: boolean;
    canRegisterStudents?: boolean;
    canUploadDocuments?: boolean;
  };
  notifications?: {
    emailEnabled?: boolean;
    smsEnabled?: boolean;
    enrollmentNotifications?: boolean;
    paymentNotifications?: boolean;
    classReminders?: boolean;
  };
};

export interface UseOrganizationSettingsResult {
  organization: Organization | null;
  loading: boolean;
  error: Error | null;
  updateOrganizationSettings: (settings: Partial<Organization>) => Promise<void>;
  updateParentPortalSettings: (settings: ParentPortalSettings) => Promise<void>;
  updateBillingSettings: (settings: Partial<BillingSettings>) => Promise<Organization | null>;
  updateClassDefaults: (settings: Partial<ClassDefaults>) => Promise<Organization | null>;
  refreshOrganizationData: () => Promise<void>;
}

export const useOrganizationSettings = (organizationId?: string): UseOrganizationSettingsResult => {
  const {
    organization,
    loading,
    error,
    setOrganization,
    refreshOrganizationData,
  } = useOrganizationCoreSettings(organizationId);

  const updateBillingSettings = useUpdateBillingSettings(organization, setOrganization, organizationId);
  const updateClassDefaults = useUpdateClassDefaults(organization, setOrganization, organizationId);
  const updateParentPortalSettings = useUpdateParentPortalSettings(organization, setOrganization, organizationId);
  const updateOrganizationSettings = useUpdateOrganizationSettings(organization, setOrganization, organizationId);

  return {
    organization,
    loading,
    error,
    updateOrganizationSettings,
    updateParentPortalSettings,
    updateBillingSettings,
    updateClassDefaults,
    refreshOrganizationData,
  };
};
